import type { ReactNode } from "react";
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  Children,
  isValidElement,
  cloneElement,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CornerDownLeft, Pencil, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatAvatar, ChatAvatarSpacer, CHAT_AVATAR_GUTTER_CLASS } from "./ChatAvatar";
import {
  DocChip,
  DocChipCollapsed,
  DocChipGroup,
  EhrChip,
  WebLinkChip,
  IssueChipGroup,
} from "./DocLink";
import type { IssueChipData } from "./DocLink";
import { FileOutputChipGroup } from "./FileOutputChip";
import type { FileOutputChipFile } from "./FileOutputChip";
import { FeedbackButtons } from "./FeedbackButtons";
import type { FeedbackRating } from "./FeedbackButtons";
import { useTypewriterEffect } from "./useTypewriterEffect";
import type { ChatTextSize } from "./types";
import { stripChoiceTags } from "@/utils/sanitization";

const INLINE_DOC_GROUP_SEPARATOR_REGEX = /^\s*[;,]\s*$/;

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export interface AttachedIssueRef {
  id: number;
  issueNumber: number;
  title: string;
}

/**
 * Parse user messages that contain attached document and/or issue metadata.
 * Detects the `[ATTACHED DOCUMENTS` and/or `[ATTACHED ISSUES` prefixes and
 * extracts the user question plus the attached doc/issue references so the
 * chat UI can render them as chips instead of raw text (WHA-3338).
 */
export function parseAttachedContextMessage(content: string): {
  userQuestion: string;
  attachedFilenames: string[];
  /** WHA-3330: doc id + filename pairs so the chip renderer can look
   *  up per-doc loading state from `chatStore.isDocLoading(id)`. */
  attachedDocuments: Array<{ id: string; filename: string }>;
  attachedIssues: AttachedIssueRef[];
} | null {
  if (!content.startsWith("[ATTACHED DOCUMENTS") && !content.startsWith("[ATTACHED ISSUES")) {
    return null;
  }

  // Extract filename + id from lines like "- filename.pdf (document_id: 123)"
  const filenameRegex = /^- (.+?) \(document_id: (\d+)\)$/gm;
  const filenames: string[] = [];
  const docs: Array<{ id: string; filename: string }> = [];
  let docMatch: RegExpExecArray | null;
  while ((docMatch = filenameRegex.exec(content)) !== null) {
    filenames.push(docMatch[1]);
    docs.push({ filename: docMatch[1], id: docMatch[2] });
  }

  // Extract issue refs from lines like "- Issue #12: Update Nortal .ifc (issue_id: 12)"
  const issueRegex = /^- Issue #(\d+): (.+?) \(issue_id: (\d+)\)$/gm;
  const issues: AttachedIssueRef[] = [];
  let issueMatch: RegExpExecArray | null;
  while ((issueMatch = issueRegex.exec(content)) !== null) {
    issues.push({
      issueNumber: Number(issueMatch[1]),
      title: issueMatch[2],
      id: Number(issueMatch[3]),
    });
  }

  // Extract user question from "User question: ..." at the end
  const questionMatch = content.match(/User question:\s*(.+)$/s);
  const userQuestion = questionMatch ? questionMatch[1].trim() : content;

  return {
    userQuestion,
    attachedFilenames: filenames,
    attachedDocuments: docs,
    attachedIssues: issues,
  };
}

/**
 * Pre-computed filename detection data.
 * Created once per filename list change, reused for all text processing.
 */
interface FilenameDetectionData {
  sortedFilenames: string[];
  filenameLookup: Map<string, string>;
  regex: RegExp | null;
}

/**
 * Build filename detection data from a list of filenames.
 * Returns sorted filenames and pre-compiled regex for efficient matching.
 */
function buildFilenameDetectionData(filenames: string[]): FilenameDetectionData {
  if (!filenames.length) {
    return { sortedFilenames: [], filenameLookup: new Map(), regex: null };
  }

  // Sort by length descending to match longer names first (e.g., "Q12.pdf" before "Q1.pdf")
  const sortedFilenames = [...filenames].sort((a, b) => b.length - a.length);
  const filenameLookup = new Map<string, string>();
  sortedFilenames.forEach((filename) => {
    filenameLookup.set(filename.toLowerCase(), filename);
  });

  // Build regex pattern: escape special chars, join with OR
  const pattern = sortedFilenames.map((f) => escapeRegex(f)).join("|");
  const regex = new RegExp(`(${pattern})`, "gi");

  return { sortedFilenames, filenameLookup, regex };
}

type TextToken =
  | { type: "text"; value: string }
  | { type: "filename"; value: string; canonicalFilename: string };

function tokenizeTextWithFilenames(
  text: string,
  detectionData: FilenameDetectionData
): TextToken[] {
  const { filenameLookup, regex } = detectionData;

  if (!regex) {
    return [{ type: "text", value: text }];
  }

  regex.lastIndex = 0;
  const tokens: TextToken[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const matchedValue = match[0];
    const startIndex = match.index;
    const endIndex = startIndex + matchedValue.length;

    if (startIndex > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, startIndex) });
    }

    const canonicalFilename = filenameLookup.get(matchedValue.toLowerCase());
    if (canonicalFilename) {
      tokens.push({
        type: "filename",
        value: matchedValue,
        canonicalFilename,
      });
    } else {
      tokens.push({ type: "text", value: matchedValue });
    }

    lastIndex = endIndex;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }

  return tokens;
}

function isInlineDocGroupSeparator(text: string): boolean {
  return INLINE_DOC_GROUP_SEPARATOR_REGEX.test(text);
}

/**
 * Detect and render document filenames in text using pre-computed detection data.
 * Matches known filenames exactly (case-insensitive) and renders them as DocChip components.
 */
function detectDocumentFilenames(
  text: string,
  detectionData: FilenameDetectionData,
  onDocLinkClick?: (filename: string) => void
): ReactNode[] {
  const tokens = tokenizeTextWithFilenames(text, detectionData);

  // If no filename tokens, return original text
  if (!tokens.some((token) => token.type === "filename")) {
    return [text];
  }

  const renderedParts: ReactNode[] = [];

  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i];

    if (token.type !== "filename") {
      renderedParts.push(token.value);
      i += 1;
      continue;
    }

    const runFilenames = [token.canonicalFilename];
    let j = i + 1;

    while (
      j + 1 < tokens.length &&
      tokens[j].type === "text" &&
      isInlineDocGroupSeparator(tokens[j].value) &&
      tokens[j + 1].type === "filename"
    ) {
      const nextFilename = tokens[j + 1];
      if (nextFilename.type !== "filename") break;
      runFilenames.push(nextFilename.canonicalFilename);
      j += 2;
    }

    if (runFilenames.length >= 3) {
      renderedParts.push(
        <DocChipCollapsed
          key={`doc-group-${i}`}
          filenames={runFilenames}
          onDocClick={(filename) => onDocLinkClick?.(filename)}
        />
      );
      i = j;
      continue;
    }

    renderedParts.push(
      <DocChip
        key={`doc-chip-${i}`}
        filename={token.canonicalFilename}
        onClick={() => onDocLinkClick?.(token.canonicalFilename)}
      />
    );

    let k = i + 1;
    while (k < j) {
      const separatorToken = tokens[k];
      const filenameToken = tokens[k + 1];

      if (separatorToken?.type === "text") {
        renderedParts.push(separatorToken.value);
      }

      if (filenameToken?.type === "filename") {
        renderedParts.push(
          <DocChip
            key={`doc-chip-${k + 1}`}
            filename={filenameToken.canonicalFilename}
            onClick={() => onDocLinkClick?.(filenameToken.canonicalFilename)}
          />
        );
      }

      k += 2;
    }

    i = j;
  }

  return renderedParts;
}

/**
 * Process text node to detect document filenames.
 */
function processTextWithFilenames(
  text: string,
  detectionData: FilenameDetectionData,
  onDocLinkClick?: (filename: string) => void
): ReactNode {
  const processed = detectDocumentFilenames(text, detectionData, onDocLinkClick);
  return processed.length === 1 ? processed[0] : <>{processed}</>;
}

/**
 * Recursively process children to detect and render document filenames.
 */
function processChildrenWithFilenames(
  children: ReactNode,
  detectionData: FilenameDetectionData,
  onDocLinkClick?: (filename: string) => void
): ReactNode {
  return Children.map(children, (child) => {
    // Process string children
    if (typeof child === "string") {
      return processTextWithFilenames(child, detectionData, onDocLinkClick);
    }

    // Recursively process element children (but skip certain elements)
    if (isValidElement(child)) {
      const elementChild = child as React.ReactElement<{ children?: ReactNode }>;
      // Skip processing for code, pre, and button elements (don't linkify code blocks)
      if (
        elementChild.type === "code" ||
        elementChild.type === "pre" ||
        elementChild.type === "button"
      ) {
        return child;
      }

      if (elementChild.props.children) {
        return cloneElement(elementChild, {
          ...elementChild.props,
          children: processChildrenWithFilenames(
            elementChild.props.children,
            detectionData,
            onDocLinkClick
          ),
        });
      }
    }

    return child;
  });
}

const TREE_CHAR_REGEX = /([├└│─┌┐┘┤┬┴┼]+)/g;

/**
 * Recursively process children to colorize tree-drawing characters in Franklin red.
 * Skips code/pre/button elements — only affects prose text.
 */
function processChildrenWithTreeChars(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string") {
      TREE_CHAR_REGEX.lastIndex = 0;
      if (!TREE_CHAR_REGEX.test(child)) return child;

      TREE_CHAR_REGEX.lastIndex = 0;
      const parts: ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      let keyIdx = 0;

      while ((match = TREE_CHAR_REGEX.exec(child)) !== null) {
        if (match.index > lastIndex) {
          parts.push(child.slice(lastIndex, match.index));
        }
        parts.push(
          <span key={`tree-${keyIdx++}`} className="font-mono text-primary">
            {match[0]}
          </span>
        );
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < child.length) {
        parts.push(child.slice(lastIndex));
      }

      return parts.length === 1 ? parts[0] : <>{parts}</>;
    }

    if (isValidElement(child)) {
      const el = child as React.ReactElement<{ children?: ReactNode }>;
      if (el.type === "code" || el.type === "pre" || el.type === "button") return child;
      if (el.props.children) {
        return cloneElement(el, {
          ...el.props,
          children: processChildrenWithTreeChars(el.props.children),
        });
      }
    }

    return child;
  });
}

const EHR_CITATION_REGEX = /\bEhitisregister(?:\s*\(EHR\))?/gi;

/**
 * Recursively process children to detect EHR citations and render them as EhrChip components.
 */
function processChildrenWithEhrCitation(children: ReactNode, ehrLink: string): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string") {
      EHR_CITATION_REGEX.lastIndex = 0;
      if (!EHR_CITATION_REGEX.test(child)) return child;

      EHR_CITATION_REGEX.lastIndex = 0;
      const parts: ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      let keyIdx = 0;

      while ((match = EHR_CITATION_REGEX.exec(child)) !== null) {
        if (match.index > lastIndex) {
          parts.push(child.slice(lastIndex, match.index));
        }
        parts.push(<EhrChip key={`ehr-${keyIdx++}`} url={ehrLink} />);
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < child.length) {
        parts.push(child.slice(lastIndex));
      }

      return parts.length === 1 ? parts[0] : <>{parts}</>;
    }

    if (isValidElement(child)) {
      const el = child as React.ReactElement<{ children?: ReactNode }>;
      if (el.type === "code" || el.type === "pre" || el.type === "button") return child;
      if (el.props.children) {
        return cloneElement(el, {
          ...el.props,
          children: processChildrenWithEhrCitation(el.props.children, ehrLink),
        });
      }
    }

    return child;
  });
}

/**
 * Props for the SidebarChatMessage component.
 */
export interface SidebarChatMessageProps {
  /** The message ID (required for feedback) */
  messageId?: string | null;
  /** The role of the message sender */
  role: "user" | "assistant";
  /** The message content */
  content: string;
  /** Responsive text size derived from widget width */
  textSize?: ChatTextSize;
  /** Callback when a document link is clicked */
  onDocLinkClick?: (filename: string) => void;
  /** Callback when an attached-issue chip is clicked (WHA-3469) */
  onIssueClick?: (issue: IssueChipData) => void;
  /** List of known document filenames for auto-detection */
  documentFilenames?: string[];
  /** Current feedback rating for this message */
  currentRating?: FeedbackRating | null;
  /** Whether feedback is being submitted for this message */
  isFeedbackLoading?: boolean;
  /** Callback to submit feedback */
  onFeedback?: (messageId: string, rating: FeedbackRating) => void;
  /** Multiple-choice options to display below the message */
  choices?: string[];
  /** Question text to display above choices */
  choiceQuestion?: string;
  /** Callback when a choice button is clicked */
  onChoiceSelect?: (choice: string) => void;
  /** Whether this message is an error */
  isError?: boolean;
  /** Whether this is the last assistant message (choices only show on last) */
  isLastAssistant?: boolean;
  /** Currently selected choice awaiting confirmation */
  pendingChoice?: string | null;
  /** Callback to confirm the pending choice */
  onChoiceConfirm?: () => void;
  /** Callback to cancel the pending choice */
  onChoiceCancel?: () => void;
  /** Callback to refresh chat (shown on error assistant messages) */
  onRefreshChat?: () => void;
  /** Refresh action label */
  refreshChatLabel?: string;
  /** Whether to animate this message with typewriter effect */
  animate?: boolean;
  /** Callback when typewriter animation completes */
  onAnimationComplete?: () => void;
  /** Callback fired periodically during animation (for scroll sync) */
  onAnimationProgress?: () => void;
  /** Whether this response used the document tool (reads from document text) */
  usedDocumentTool?: boolean;
  /** EHR link URL to display as an external chip */
  ehrLink?: string | null;
  /** Visual tone for assistant text */
  tone?: "default" | "intermediate";
  /** Optional incoming-bubble treatment for a real assistant message. */
  appearance?: "plain" | "bubble";
  /** Use the compact incoming-bubble layout for the empty-state greeting. */
  compactBubble?: boolean;
  /** Show Franklin's mark in the bubble's gutter. Off for a bubble that
   *  continues a turn whose mark is already on screen — the gutter is still
   *  held open, so the bubbles stay on one left edge. */
  showAvatar?: boolean;
  /** Render that mark as the loading animation: this bubble is still being
   *  written (a flushed thought or a streaming draft). */
  avatarAnimated?: boolean;
  /** Whether this is the last user message in the thread (WHA-2637) */
  isLastUserMessage?: boolean;
  /** Callback when user submits an edited message (WHA-2637) */
  onEditMessage?: (editedText: string) => void;
  /** Files generated by the agent during this turn — rendered as download chips (WHA-3118) */
  generatedFiles?: FileOutputChipFile[];
  /** WHA-3330: doc ids currently mid-extraction. Chips on this user
   *  message whose ids are in this set render with the loader spinner
   *  (same chip moves from the input area into the message bubble on
   *  submit and keeps spinning until extraction lands). */
  loadingDocIds?: ReadonlySet<string>;
}

/**
 * SidebarChatMessage - Individual message component for the sidebar chat.
 *
 * Renders differently based on the message role:
 * - Assistant (bot) messages: Left-aligned plain text, with an incoming-bubble option
 * - User messages: Right-aligned with gray bubble
 *
 * Document filenames are automatically detected and rendered as clickable DocChip components.
 *
 * Visual specs:
 * - Bot messages: Plain text on body background, markdown support
 * - User bubble: background neutral-100
 * - User bubble radius: 10px (rounded on all corners except top-right)
 * - Text color: neutral-900
 */
export function SidebarChatMessage({
  messageId,
  role,
  content,
  textSize = "sm",
  onDocLinkClick,
  onIssueClick,
  documentFilenames = [],
  currentRating = null,
  isFeedbackLoading = false,
  onFeedback,
  choices = [],
  choiceQuestion = "",
  onChoiceSelect,
  isError = false,
  isLastAssistant = false,
  pendingChoice = null,
  onChoiceConfirm,
  onChoiceCancel,
  onRefreshChat,
  refreshChatLabel = "Refresh chat",
  animate = false,
  onAnimationComplete,
  onAnimationProgress,
  ehrLink,
  tone = "default",
  appearance = "plain",
  compactBubble = false,
  showAvatar = true,
  avatarAnimated = false,
  isLastUserMessage = false,
  onEditMessage,
  generatedFiles = [],
  loadingDocIds,
}: SidebarChatMessageProps) {
  const isAssistant = role === "assistant";
  // Every sent message uses the same responsive type scale. `compactBubble`
  // changes only the greeting's layout, never Franklin's message size.
  const textSizeClass = textSize === "lg" ? "text-[15px]" : "text-[14px]";
  const isIntermediateTone = tone === "intermediate";
  const assistantTextColorClass = isIntermediateTone ? "text-neutral-700" : "text-neutral-900";
  const assistantHeadingColorClass = isIntermediateTone
    ? "prose-headings:text-neutral-800"
    : "prose-headings:text-neutral-900";
  const assistantStrongColorClass = isIntermediateTone ? "text-neutral-800" : "text-neutral-900";
  const assistantCodeColorClass = isIntermediateTone ? "text-neutral-800" : "text-neutral-900";
  const assistantListMarkerClass = isIntermediateTone
    ? "prose-li:marker:text-neutral-500"
    : "prose-li:marker:text-neutral-600";

  // Typewriter effect for animated messages
  const { displayedText, isAnimating } = useTypewriterEffect(content, animate);

  // Notify parent when animation completes
  const prevIsAnimatingRef = useRef(isAnimating);
  useEffect(() => {
    if (prevIsAnimatingRef.current && !isAnimating && animate) {
      onAnimationComplete?.();
    }
    prevIsAnimatingRef.current = isAnimating;
  }, [isAnimating, animate, onAnimationComplete]);

  // Scroll sync during animation
  useEffect(() => {
    if (!isAnimating || !onAnimationProgress) return;
    const id = setInterval(onAnimationProgress, 100);
    return () => clearInterval(id);
  }, [isAnimating, onAnimationProgress]);

  // Use displayed text for assistant messages when animating
  const renderedContent = isAssistant && animate ? displayedText : content;
  const hideInteractiveElements = animate && isAnimating;

  // Memoize filename detection data (sorted filenames + compiled regex)
  // This prevents rebuilding the regex on every text node during markdown rendering
  const detectionData = useMemo(
    () => buildFilenameDetectionData(documentFilenames),
    [documentFilenames]
  );

  // Process children to auto-detect document filenames and EHR citations
  const processChildren = useCallback(
    (children: ReactNode) => {
      let result = children;
      if (detectionData.regex) {
        result = processChildrenWithFilenames(result, detectionData, onDocLinkClick);
      }
      // Colorize tree-drawing characters in Franklin red
      result = processChildrenWithTreeChars(result);
      // Always detect EHR citations — use specific building link or generic EHR homepage
      const resolvedEhrLink = ehrLink ?? "https://livekluster.ehr.ee/ui/ehr/v1/building";
      result = processChildrenWithEhrCitation(result, resolvedEhrLink);
      return result;
    },
    [detectionData, onDocLinkClick, ehrLink]
  );

  // Memoize the components object to ensure ReactMarkdown picks up updates
  const markdownComponents = useMemo(
    () => ({
      table: (props: React.ComponentPropsWithoutRef<"table">) => (
        <div className="my-2 overflow-x-auto">
          <table
            className="w-auto table-auto border-collapse [&_td]:border [&_td]:border-neutral-200 [&_td]:px-2 [&_td]:py-1.5 [&_td]:align-top [&_th]:border [&_th]:border-neutral-200 [&_th]:bg-neutral-100 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:align-top [&_th]:font-semibold [&_tr:nth-child(even)]:bg-neutral-50"
            {...props}
          />
        </div>
      ),
      blockquote: ({ children, ...props }: React.ComponentPropsWithoutRef<"blockquote">) => (
        <blockquote
          className="my-2 border-l-4 border-neutral-300 bg-neutral-50 py-1 pl-3 pr-2 italic text-neutral-700"
          {...props}
        >
          {processChildren(children)}
        </blockquote>
      ),
      hr: (props: React.ComponentPropsWithoutRef<"hr">) => (
        <hr className="my-4 border-t border-neutral-200" {...props} />
      ),
      h1: ({ children, ...props }: React.ComponentPropsWithoutRef<"h1">) => (
        <h1 className="mb-2 mt-3 text-[1em] font-semibold leading-snug first:mt-0" {...props}>
          {processChildren(children)}
        </h1>
      ),
      h2: ({ children, ...props }: React.ComponentPropsWithoutRef<"h2">) => (
        <h2 className="mb-2 mt-3 text-[1em] font-semibold leading-snug first:mt-0" {...props}>
          {processChildren(children)}
        </h2>
      ),
      h3: ({ children, ...props }: React.ComponentPropsWithoutRef<"h3">) => (
        <h3 className="mb-1.5 mt-2.5 text-[1em] font-semibold leading-snug first:mt-0" {...props}>
          {processChildren(children)}
        </h3>
      ),
      ul: (props: React.ComponentPropsWithoutRef<"ul">) => (
        <ul className="my-2 list-disc pl-4" {...props} />
      ),
      ol: (props: React.ComponentPropsWithoutRef<"ol">) => (
        <ol className="my-2 list-decimal pl-4" {...props} />
      ),
      p: ({ children, ...props }: React.ComponentPropsWithoutRef<"p">) => (
        <p className="mb-2 mt-0 leading-relaxed last:mb-0" {...props}>
          {processChildren(children)}
        </p>
      ),
      li: ({ children, ...props }: React.ComponentPropsWithoutRef<"li">) => (
        <li className="my-0.5" {...props}>
          {processChildren(children)}
        </li>
      ),
      td: ({ children, ...props }: React.ComponentPropsWithoutRef<"td">) => (
        <td {...props}>{processChildren(children)}</td>
      ),
      th: ({ children, ...props }: React.ComponentPropsWithoutRef<"th">) => (
        <th {...props}>{processChildren(children)}</th>
      ),
      strong: ({ children, ...props }: React.ComponentPropsWithoutRef<"strong">) => (
        <strong className={cn("font-semibold", assistantStrongColorClass)} {...props}>
          {processChildren(children)}
        </strong>
      ),
      a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<"a">) => {
        // Render http(s) links as styled chips with favicons
        if (href && (href.startsWith("http://") || href.startsWith("https://"))) {
          const label =
            typeof children === "string"
              ? children
              : Array.isArray(children)
                ? children.filter((c): c is string => typeof c === "string").join("")
                : undefined;
          return <WebLinkChip url={href} label={label !== href ? label : undefined} />;
        }
        // Fallback for non-http links
        return (
          <a
            href={href}
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            {processChildren(children)}
          </a>
        );
      },
      code: ({
        className,
        children,
        ...props
      }: React.ComponentPropsWithoutRef<"code"> & { className?: string }) => {
        const isInline = !className;
        if (isInline) {
          return (
            <code
              className={cn(
                "rounded bg-neutral-200 px-1 py-0.5 text-[0.9em]",
                assistantCodeColorClass
              )}
              {...props}
            >
              {children}
            </code>
          );
        }
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
      pre: (props: React.ComponentPropsWithoutRef<"pre">) => (
        <pre
          className="my-2 overflow-x-auto rounded border border-neutral-200 bg-neutral-100 p-2 text-[0.9em] text-neutral-800"
          {...props}
        />
      ),
    }),
    [assistantCodeColorClass, assistantStrongColorClass, processChildren]
  );

  // Parse attached docs/issues from user message (if present)
  // Must be called before any early returns to satisfy Rules of Hooks
  const parsedAttachedContext = useMemo(() => parseAttachedContextMessage(content), [content]);

  // WHA-3330: when the user submitted with one or more docs that were
  // still being extracted, those chips "moved" from the input area into
  // this message bubble. Render them with a spinner until extraction
  // lands. `loadingDocIds` is passed from the observer parent
  // (SidebarChatMessages → from SidebarChat which reads chatStore); we
  // re-render when the prop reference changes.
  const loadingFilenames = useMemo(() => {
    if (!parsedAttachedContext || !loadingDocIds || loadingDocIds.size === 0) return undefined;
    const set = new Set<string>();
    for (const doc of parsedAttachedContext.attachedDocuments) {
      if (loadingDocIds.has(doc.id)) set.add(doc.filename);
    }
    return set.size > 0 ? set : undefined;
  }, [parsedAttachedContext, loadingDocIds]);

  // Keyboard shortcuts: A/B/C/D toggle corresponding choice, Enter confirms
  useEffect(() => {
    if (!isLastAssistant || !choices?.length) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      // Enter confirms the pending choice
      if (e.key === "Enter" && pendingChoice) {
        e.preventDefault();
        onChoiceConfirm?.();
        return;
      }

      const index = e.key.toUpperCase().charCodeAt(0) - 65;
      if (index < 0 || index >= choices.length) return;
      e.preventDefault();
      const choice = choices[index];
      pendingChoice === choice ? onChoiceCancel?.() : onChoiceSelect?.(choice);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLastAssistant, choices, pendingChoice, onChoiceSelect, onChoiceCancel, onChoiceConfirm]);

  // Inline edit state — must be declared before any early returns (WHA-2637)
  const [isEditing, setIsEditing] = useState(false);
  const displayText = parsedAttachedContext ? parsedAttachedContext.userQuestion : content;
  const [editText, setEditText] = useState(displayText);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus and auto-resize textarea when entering edit mode
  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      editTextareaRef.current.focus();
      editTextareaRef.current.style.height = "auto";
      editTextareaRef.current.style.height = `${editTextareaRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const handleStartEdit = useCallback(() => {
    setEditText(displayText);
    setIsEditing(true);
  }, [displayText]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditText(displayText);
  }, [displayText]);

  const handleSubmitEdit = useCallback(() => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === displayText) {
      setIsEditing(false);
      return;
    }
    setIsEditing(false);
    onEditMessage?.(trimmed);
  }, [editText, displayText, onEditMessage]);

  if (isAssistant) {
    const handleFeedback = (rating: FeedbackRating) => {
      if (!messageId || !onFeedback) return;
      onFeedback(messageId, rating);
    };

    // Error message - distinct styling with red accent. It is still one of
    // Franklin's rows, so a bubble thread keeps it on the bubbles' left edge:
    // the gutter is padded rather than marked, since the failure is the app
    // speaking, not him.
    if (isError) {
      return (
        <div className={cn("group", appearance === "bubble" && CHAT_AVATAR_GUTTER_CLASS)}>
          <div className={cn("text-red-600", textSizeClass)}>
            <span className="whitespace-pre-line">{content}</span>
          </div>
          {onRefreshChat ? (
            <button
              type="button"
              onClick={onRefreshChat}
              className="mt-2 rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
            >
              {refreshChatLabel}
            </button>
          ) : null}
        </div>
      );
    }

    // Assistant message. The incoming-bubble treatment carries Franklin's mark
    // in its gutter — the chat's "who is speaking" icon — so a reply reads as
    // something he has said rather than as page copy.
    return (
      <div
        className={cn(
          "group",
          appearance === "bubble" && "flex items-start gap-2",
          appearance === "bubble" && compactBubble && "w-full"
        )}
        data-message-appearance={appearance}
      >
        {appearance === "bubble" &&
          (showAvatar ? <ChatAvatar animated={avatarAnimated} /> : <ChatAvatarSpacer />)}
        <div
          className={cn(
            appearance === "bubble" &&
              (compactBubble ? "w-fit min-w-0 max-w-full" : "min-w-0 max-w-[85%]")
          )}
        >
          <div
            className={cn(
              assistantTextColorClass,
              appearance === "bubble" && "rounded-[12px] rounded-tl-[3px] bg-[#f7f7f8] px-3 py-2.5"
            )}
          >
            <div
              className={cn(
                "prose max-w-none dark:prose-invert",
                assistantHeadingColorClass,
                "prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800",
                "prose-code:before:content-none prose-code:after:content-none",
                "prose-pre:bg-neutral-100 prose-pre:text-neutral-800",
                assistantListMarkerClass,
                "last:prose-p:mb-0",
                textSizeClass
              )}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {stripChoiceTags(renderedContent)}
              </ReactMarkdown>
            </div>
          </div>
          {!hideInteractiveElements && generatedFiles.length > 0 ? (
            <div className="mt-2">
              <FileOutputChipGroup files={generatedFiles} />
            </div>
          ) : null}
          {isLastAssistant && !hideInteractiveElements && choices && choices.length > 0 && (
            <div className="mt-3 flex flex-col gap-1.5">
              {/* Question header */}
              {choiceQuestion && (
                <p className={cn("mb-0.5 font-medium text-neutral-800", textSizeClass)}>
                  {stripChoiceTags(choiceQuestion)}
                </p>
              )}

              {/* Choice buttons */}
              {choices.map((choice, i) => {
                const isSelected = pendingChoice === choice;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => (isSelected ? onChoiceCancel?.() : onChoiceSelect?.(choice))}
                    className={cn(
                      "group/choice flex w-full items-center overflow-hidden rounded-lg",
                      "border transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400",
                      isSelected
                        ? "border-primary ring-1 ring-primary/20"
                        : "border-neutral-300 hover:border-neutral-400"
                    )}
                  >
                    <span
                      className={cn(
                        "flex w-8 flex-shrink-0 items-center justify-center self-stretch",
                        "text-sm font-semibold",
                        isSelected ? "bg-primary text-white" : "bg-neutral-800 text-primary"
                      )}
                    >
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span
                      className={cn(
                        "flex-1 px-3 py-2 text-left transition-colors",
                        textSizeClass,
                        isSelected
                          ? "bg-primary/5 font-medium text-neutral-800"
                          : "bg-neutral-50 text-neutral-600 group-hover/choice:bg-neutral-100"
                      )}
                    >
                      {stripChoiceTags(choice)}
                    </span>
                  </button>
                );
              })}

              {/* Permanent action bar */}
              <div className="mt-1 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onChoiceCancel}
                  disabled={!pendingChoice}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs transition-colors",
                    pendingChoice
                      ? "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                      : "cursor-default text-neutral-300"
                  )}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onChoiceConfirm}
                  disabled={!pendingChoice}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    "flex items-center gap-1.5",
                    pendingChoice
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "cursor-default bg-neutral-200 text-neutral-400"
                  )}
                >
                  Send
                  <CornerDownLeft className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
          {messageId && onFeedback && !hideInteractiveElements ? (
            <div className="mt-2">
              <FeedbackButtons
                rating={currentRating}
                isLoading={isFeedbackLoading}
                onThumbsUp={() => handleFeedback("positive")}
                onThumbsDown={() => handleFeedback("negative")}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // User message - right-aligned, no avatar
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="group/user relative max-w-[85%]">
        {isLastUserMessage && onEditMessage && !isEditing && (
          <button
            onClick={handleStartEdit}
            className="absolute -left-8 top-1/2 -translate-y-1/2 rounded p-1 opacity-0 transition-opacity hover:bg-neutral-200 group-hover/user:opacity-100"
            aria-label="Edit message"
          >
            <Pencil className="h-3.5 w-3.5 text-neutral-500" />
          </button>
        )}
        <div
          className={cn(
            "rounded-[10px] rounded-tr-none bg-neutral-300/50 px-3 py-2",
            "text-neutral-900",
            textSizeClass
          )}
        >
          {isEditing ? (
            <div className="flex flex-col gap-1.5">
              <textarea
                ref={editTextareaRef}
                value={editText}
                onChange={(e) => {
                  setEditText(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitEdit();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    handleCancelEdit();
                  }
                }}
                className={cn(
                  "w-full resize-none rounded bg-white/80 px-2 py-1 outline-none ring-1 ring-primary/30 focus:ring-primary/60",
                  textSizeClass
                )}
                rows={1}
              />
              <div className="flex justify-end gap-1">
                <button
                  onClick={handleCancelEdit}
                  className="rounded p-1 text-neutral-500 hover:bg-neutral-200"
                  aria-label="Cancel edit"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={handleSubmitEdit}
                  className="rounded p-1 text-primary hover:bg-primary/10"
                  aria-label="Submit edit"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <span className="whitespace-pre-line">{displayText}</span>
          )}
        </div>
      </div>
      {parsedAttachedContext && parsedAttachedContext.attachedFilenames.length > 0 && (
        <DocChipGroup
          filenames={parsedAttachedContext.attachedFilenames}
          onDocClick={(filename) => onDocLinkClick?.(filename)}
          maxVisible={3}
          loadingFilenames={loadingFilenames}
          className="justify-end"
        />
      )}
      {parsedAttachedContext && parsedAttachedContext.attachedIssues.length > 0 && (
        <IssueChipGroup
          variant="message"
          className="justify-end"
          issues={parsedAttachedContext.attachedIssues.map((issue) => ({
            id: issue.id,
            issueNumber: issue.issueNumber,
            title: issue.title,
          }))}
          onIssueClick={(issue) => onIssueClick?.(issue)}
        />
      )}
    </div>
  );
}

SidebarChatMessage.displayName = "SidebarChatMessage";
